import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Formik, FormikProps, yupToFormErrors } from "formik";
import codes from "country-calling-code";
import { Button, CheckboxInput, ErrorMessage, Icon, Input, InputPicker, Select, Tooltip } from "@shared/components";
import { Account, RoleName, User } from "@shared/models";
import { ClientError, CreateClientDto } from "@containers/Contact/interfaces";
import { useDispatch, useSelector } from "react-redux";
import { getUserExistence } from "@containers/Auth/store/selectors";
import { getActiveAccount } from "@containers/Account/store/selectors";
import { checkUser, clearUserExistence } from "@containers/Auth/store/actions";
import * as accountStore from "@containers/Account/store";
import { AccessControl } from "@shared/components/AccessControl";
import { MODAL_TYPES, USER_PERMISSION } from "@shared/constants";
import { actions, selectors as sharedSelectors } from "@shared/store";
import { EditableUserRoles } from "@containers/Contact/constants";
import { ModalSize } from "@shared/interfaces";
import { ReactComponent as ArrowRight } from "@assets/files/icons/general/arrow-right-blue.svg";
import { ReactComponent as ChevronDown } from "@assets/files/icons/general/chevron-down.svg";
import classnames from "classnames";
import { getTrimmedValues } from "@shared/validations";
import { getFullName } from "@shared/utils";
import { checkIfAdmin } from "@shared/utils/ACL";

import { ClientFormShape, getInitValues, prepareSubmitValue, validationSchema } from "./formHelpers";
import { useServerErrors } from "./useServerErrors";
import { AdditionalEmailsField } from "./AdditionalEmailsField";

import "./index.scss";

interface ClientFormProps {
  user: User | null;
  account: Account;
  onChangeForm: () => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: Partial<CreateClientDto>) => void;
  changeClientAccountStatus: () => void;
  isSelectedClientActiveInContact: boolean;
}

const CODES_OVERRIDES: Record<string, string> = {
  "380": "38",
};

const getCode = (code: string) => CODES_OVERRIDES[code] || code;

const isAllowedAdminEmail = (email?: string) => {
  return email?.toLowerCase().endsWith("@dataflik.com") ?? false;
};

export const ClientForm: FC<ClientFormProps> = (props) => {
  const dispatch = useDispatch();
  const {
    user,
    account,
    onChangeForm,
    onCloseForm,
    onSubmitForm,
    changeClientAccountStatus,
    isSelectedClientActiveInContact,
  } = props;

  const activeUser = useSelector(sharedSelectors.getUserDetails());
  const activeAccount = useSelector(getActiveAccount());
  const isActiveUserPrimary = activeAccount?.user_id === activeUser?.id;
  const isAdmin = checkIfAdmin(activeUser!);

  const [assignUser, setAssignUser] = useState(false);

  const formValues = useMemo(() => getInitValues(account, user), [user, account]);
  const formikRef = useRef<FormikProps<ClientFormShape>>(null);

  const userExistence = useSelector(getUserExistence());
  const isPrimaryUser = (user && user.id === account?.primary_user?.id) as boolean;

  useEffect(() => {
    if (userExistence !== null && formikRef.current) {
      dispatch(clearUserExistence());
      if (userExistence) {
        dispatch(
          actions.showModal({
            type: MODAL_TYPES.CONFIRM,
            props: {
              heading: "Existing user",
              content: `This user already exists in the system. Please confirm that you would like to add the user to the ${account?.name}.`,
              cancelBtnText: "Cancel",
              successBtnText: "Confirm",
              onSuccess: () => {
                setAssignUser(true);
                formikRef.current?.submitForm();
              },
            },
          }),
        );
      } else {
        setAssignUser(true);
        formikRef.current.submitForm();
      }
    }
  }, [user, account, dispatch, userExistence]);

  useServerErrors({
    formikRef,
    errorType: user ? ClientError.updateClientError : ClientError.createClientError,
  });

  const handleCheckUserEmail = (values: ClientFormShape) => {
    dispatch(checkUser.request({ email: values.email, noRedirect: true }));
  };

  const userCanBeMoved = !isPrimaryUser && (account?.users?.length || 0) > 1;
  const accountUser = account.users?.find((u) => u.id === user?.id);
  const isDisabled = !!user && !accountUser?.is_active_in_account;

  const moveUserToAnotherCompany = () =>
    userCanBeMoved &&
    dispatch(
      actions.showModal({
        type: MODAL_TYPES.MOVE_USER_TO_ANOTHER_COMPANY,
        closeIcon: true,
        className: "move-user-to-another-company",
        size: ModalSize.RESIZABLE,
        props: {
          data: {
            user,
            account,
            onCloseForm,
          },
        },
      }),
    );

  const getAccount = () => dispatch(accountStore.actions.getAccount.request({ id: account.id }));

  const makeUserPrimary = () => {
    dispatch(
      actions.showModal({
        type: MODAL_TYPES.CONFIRM,
        props: {
          heading: "Make User Primary",
          content: `Are you sure you want to make user ${getFullName(user)} primary?`,
          cancelBtnText: "Cancel",
          successBtnText: "Confirm",
          successBtnStyle: "accent-green",
          cancelBtnStyle: "blue-bordered",
          onSuccess: () => {
            dispatch(
              accountStore.actions.updatePrimaryUser.request({
                user_id: user?.id as number,
                account_id: account?.id,
                callback: getAccount,
              }),
            );
          },
        },
      }),
    );
  };

  return (
    <Formik
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={async (values, { setSubmitting }) => {
        const trimmed = await getTrimmedValues(validationSchema, values);

        if (user || assignUser) {
          setAssignUser(false);
          onSubmitForm(prepareSubmitValue(trimmed, account));
          setSubmitting(false);
        } else if (account?.users?.some(({ email }) => email === trimmed.email)) {
          formikRef.current?.setFieldError("email", "User already exists");
        } else {
          handleCheckUserEmail(trimmed);
        }
      }}
      initialValues={formValues}
      validate={async (values) => {
        try {
          await validationSchema.validate(values, { abortEarly: false });
        } catch (error) {
          return yupToFormErrors(error);
        }
      }}
      innerRef={formikRef}
    >
      {({ values, handleChange: formikHandleChange, handleSubmit, errors, touched }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleChange = (e: any) => {
          onChangeForm();
          return formikHandleChange(e);
        };

        return (
          <div
            className={classnames("client-form", {
              disabled: isDisabled,
            })}
          >
            <form onSubmit={handleSubmit}>
              <div className="client-form-wrapper">
                <div className="client-form-header">
                  <div className="client-form-header-left">{user ? "Edit" : "New"} User</div>

                  {!!user && isAdmin && (
                    <div className="client-form-header-right">
                      <Icon type="details" />

                      <div className="actions-popup">
                        <div
                          className={classnames("actions-popup-item", {
                            disabled: !userCanBeMoved,
                          })}
                          onClick={moveUserToAnotherCompany}
                        >
                          <div className="actions-popup-item-wrapper">
                            <ArrowRight /> Move User to Other Company
                          </div>
                        </div>
                        <div
                          className={classnames("actions-popup-item", {
                            disabled: isPrimaryUser || !isSelectedClientActiveInContact,
                          })}
                          onClick={makeUserPrimary}
                        >
                          <div className="actions-popup-item-wrapper">
                            <Icon type="star-blue" className="icon" /> Make User Primary
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="horizontal-divider"></div>
                <div className="client-form-content">
                  {isPrimaryUser && (
                    <div className="primary-user-label">
                      <div className="content">
                        <Icon type="star" className="icon" />
                        Primary User
                      </div>
                    </div>
                  )}
                  <div className="user-name-input-group">
                    <div className="user-name-input-field-wrapper">
                      <Input
                        type="text"
                        name="first_name"
                        label="First Name"
                        value={values.first_name}
                        onChange={handleChange}
                        disabled={isDisabled}
                      />
                      <ErrorMessage isTouched={touched["first_name"]} error={errors["first_name"]} />
                    </div>
                    <div className="user-name-input-field-wrapper">
                      <Input
                        type="text"
                        name="last_name"
                        label="Last Name"
                        value={values.last_name}
                        onChange={handleChange}
                        disabled={isDisabled}
                      />
                      <ErrorMessage isTouched={touched["last_name"]} error={errors["last_name"]} />
                    </div>
                  </div>

                  <div className="phone">
                    <InputPicker
                      disabled={isDisabled}
                      searchable
                      cleanable={false}
                      name="phone_country_code"
                      className="select-picker"
                      label="Country Code"
                      useContainer
                      disableContainerStyles
                      value={values.phone_country_code}
                      data={codes.map((i) => ({
                        value: getCode(i.countryCodes[0]),
                        label: `${i.country} (+${getCode(i.countryCodes[0])})`,
                      }))}
                      onChange={(value) => handleChange({ target: { id: "phone_country_code", value } })}
                    />

                    <div className="phone_number">
                      <Input
                        disabled={isDisabled}
                        type="phone"
                        name="phone_number"
                        label="Phone Number"
                        value={values.phone_number?.toString()}
                        onChange={handleChange}
                      />
                      <ErrorMessage isTouched={touched["phone_number"]} error={errors["phone_number"]} />
                    </div>
                  </div>

                  <div className="horizontal-divider"></div>

                  <div className="primary-email">
                    <Input
                      type="text"
                      name="email"
                      disabled={isDisabled}
                      label="Primary Email"
                      value={values.email}
                      onChange={handleChange}
                    />
                  </div>
                  <ErrorMessage isTouched={touched["email"]} error={errors["email"]} />

                  <div className="horizontal-divider"></div>

                  <AdditionalEmailsField disabled={isDisabled} onChangeForm={onChangeForm} />

                  <div className="horizontal-divider"></div>

                  <div
                    className={classnames("roles", {
                      "without-values": !values.roles?.length,
                    })}
                  >
                    <label className="label">Roles</label>
                    <Select
                      isDisabled={isDisabled}
                      value={false}
                      dropdownIcon={<ChevronDown />}
                      isSearchable
                      options={EditableUserRoles.filter((r) => isAdmin || !r.onlyForAdmin).filter((r) => {
                        return (
                          !r.readOnly &&
                          (r.value === RoleName.admin
                            ? !values.roles?.includes(RoleName.admin) && isAllowedAdminEmail(values?.email)
                            : !values.roles?.includes(r.value))
                        );
                      })}
                      placeholder="Select"
                      onChange={(value) =>
                        handleChange({
                          target: {
                            id: "roles",
                            value: values.roles?.concat(value as RoleName).filter((r) => {
                              if (!isAllowedAdminEmail(values?.email)) return r !== RoleName.admin;
                              if (value === RoleName.admin) return ![RoleName.user, RoleName.super_admin].includes(r);
                              if (value === RoleName.user) return ![RoleName.admin, RoleName.super_admin].includes(r);
                              return true;
                            }),
                          },
                        })
                      }
                      noOptionsMessage={() => "No matches found"}
                    />
                    {!!values.roles?.length && (
                      <div className="role-values">
                        {EditableUserRoles.filter((r) => values.roles?.includes(r.value)).map((r) => (
                          <div className="role-value" key={r.value}>
                            {r.label}{" "}
                            {(!r.readOnly || isAdmin) && (
                              <Icon
                                type="clear"
                                onClick={() =>
                                  handleChange({
                                    target: {
                                      id: "roles",
                                      value: values.roles?.filter((role) => r.value !== role),
                                    },
                                  })
                                }
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <ErrorMessage isTouched={!!touched.roles} error={errors.roles as string} />
                  </div>

                  <div className="horizontal-divider"></div>

                  <div>
                    <CheckboxInput
                      disabled={isDisabled}
                      name="email_on_lead_creation"
                      value={Number(values.email_on_lead_creation)}
                      onChange={handleChange}
                      label="Notify about new PPL via Email"
                    />

                    <CheckboxInput
                      disabled={isDisabled}
                      name="sms_on_lead_creation"
                      value={Number(values.sms_on_lead_creation)}
                      onChange={handleChange}
                      label="Notify about new PPL via SMS"
                    />
                  </div>
                </div>
                {user && (
                  <div className="client-form-footer">
                    <div
                      className={`client-form-footer-left ${isSelectedClientActiveInContact ? "active" : "inactive"}`}
                    >
                      <span>{isSelectedClientActiveInContact ? "active" : "inactive"}</span>
                    </div>
                    <div className="client-form-footer-right">
                      <AccessControl allow={isActiveUserPrimary} permission={USER_PERMISSION.DELETE_USER}>
                        {isSelectedClientActiveInContact && (
                          <Tooltip
                            position="top-end"
                            content={
                              isPrimaryUser ? (
                                <div className="deactivate-user-tooltip">
                                  Deactivation is not allowed for the primary user.
                                </div>
                              ) : null
                            }
                          >
                            <Button
                              type="button"
                              buttonStyle="clear-red"
                              disabled={isPrimaryUser}
                              onClick={changeClientAccountStatus}
                            >
                              Deactivate User
                            </Button>
                          </Tooltip>
                        )}
                      </AccessControl>
                      <AccessControl allow={isActiveUserPrimary} permission={USER_PERMISSION.ACTIVATE_USER}>
                        {!isSelectedClientActiveInContact && (
                          <Button type="button" buttonStyle="form-cancel" onClick={changeClientAccountStatus}>
                            Activate User
                          </Button>
                        )}
                      </AccessControl>
                    </div>
                  </div>
                )}
              </div>
              <div className="client-form-actions">
                <Button type="button" buttonStyle="form-cancel" onClick={onCloseForm}>
                  Cancel
                </Button>
                <Button type="submit" buttonStyle="accent-green" disabled={isDisabled}>
                  Save
                </Button>
              </div>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};
